import { IInputAttachable } from "client/Forms/attachable";
import {
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import classNames from "classnames";
import {
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./ResultMapperFormResult.module.less";
import { Space } from "client/Components/Space/Space";
import { ValidationError } from "client/Components/ValidationError/ValidationError";

type ResultKind =
	| "empty"
	| "warning"
	| "success";

export type ResultMapperFormResultProps<TValue, TResultValue> = {
	attachable: IInputAttachable<TValue>;
	hasResult: boolean;
	resultValue: TResultValue;
};

export const ResultMapperFormResult = <TValue, TResultValue>(
	props: PropsWithChildren<ResultMapperFormResultProps<TValue, TResultValue>>
): ReactElement => {
	const {
		attachable,
		hasResult,
		resultValue,
		children
	} = props;

	const {
		value,
		validation
	} = attachable;

	const isValid: boolean = validation.isValid();

	const [
		kind,
		resultContent
	] = useMemo(
		(): [
			kind: ResultKind,
			resultContent: ReactElement
		] => {
			if (hasResult
				&& isNotNil(resultValue)
			) {
				return [
					"success",
					(
						<>
							{localize("_Result")}:
							<Space />
							{children ?? resultValue}
						</>
					)
				];
			}

			if (!hasResult
				|| isNil(value)
				|| (isNotNil(value) && !value)
			) {
				return [
					"empty",
					(
						<>
							{localize("_Empty")}
						</>
					)
				];
			}

			return [
				"warning",
				(
					<>
						{localize("_Result")}:
						<Space />
						{localize("_None")}
					</>
				)
			];
		},
		[
			children,
			hasResult,
			resultValue,
			value
		]
	);

	return isValid
		? (
			<pre
				{...setTrackingId(attachable.reducer.path, "__preview")}
				className={classNames(
					styles.result,
					resultKindToStyle[kind]
				)}
			>
				{resultContent}
			</pre>
		)
		: (
			<ValidationError {...attachable.attachToValidationError()} />
		);
};
ResultMapperFormResult.displayName = "ResultMapperFormResult";

const resultKindToStyle: Record<ResultKind, string> = {
	"empty": styles.result_empty,
	"warning": styles.result_warning,
	"success": styles.result_success,
};